<template>
  <div class="view-mandatory-checklist">
    <cs-button
      :id="getCompleteChecklistId"
      :disabled="dialog"
      :loading="dialog"
      :class="btnClass"
      :left-icon="checklistCommentIconDisplayed ? 'mdi-chat' : 'mdi-check-circle-outline'"
      label="Complete Checklist"
      @click.stop="getData()"
    >
    </cs-button>
    <cs-form-dialog
      id="dlg-view-mandatory-checklist"
      v-model="dialog"
      :body-id="getDlgBodyId"
      heading="Complete Checklist"
      :primary-action="{
        label: 'Save',
        eventName: 'primary-click',
        disabled: !formControlEnabled
      }"
      :secondary-action1="{
        label: 'Close',
        eventName: 'secondary1-click'
      }"
      @primary-click="save"
      @secondary1-click="close"
    >
      <template #cs-form-dialog-content>
        <div class="checklist view-mandatory-checklist-policy">
          <v-list v-if="checklist.title !== 'undefined' && dialog" density="compact" slim>
            <v-list-subheader>
              <h1>{{ checklist.title }}</h1>
            </v-list-subheader>
            <v-divider thickness="3px" opacity="1" class="header-divider"></v-divider>
            <v-list v-for="(item, index) in changedChildren" :key="index" density="compact" slim>
              <div v-if="item.contentType === 'form-group'">
                <div v-if="item.label.indexOf(removeLabel) !== 0">
                  <stb-markdown :source="item.label" />
                </div>
                <div v-for="(option, childIndex) in item.children" :key="childIndex">
                  <checklist-item
                    v-model="option.ticked"
                    :checklist-item="option"
                    :label="option.label"
                    :disabled="!formControlEnabled"
                    :checkbox-id="`view-mandatory-checklist--${String(checklist.title).replaceAll(
                      ' ',
                      '-'
                    )}--form-group-checkbox--${childIndex}`"
                    :comment-btn-id="`view-mandatory-checklist--${String(
                      checklist.title
                    ).replaceAll(' ', '-')}--form-group-checkbox-comment--${childIndex}`"
                    @markdown-error="markdownError = true"
                    @update:model-value="checklistOnChanged"
                  />
                </div>
              </div>
              <div v-else-if="item.contentType === 'checkbox'">
                <checklist-item
                  v-model="item.ticked"
                  :checklist-item="item"
                  :label="item.label"
                  :disabled="!formControlEnabled"
                  :checkbox-id="`view-mandatory-checklist--${String(checklist.title).replaceAll(
                    ' ',
                    '-'
                  )}--checkbox--${index}`"
                  :comment-btn-id="`view-mandatory-checklist--${String(checklist.title).replaceAll(
                    ' ',
                    '-'
                  )}--checkbox-comment--${index}`"
                  @markdown-error="markdownError = true"
                  @update:model-value="checklistOnChanged"
                />
              </div>
              <div v-else>
                <div>
                  <stb-markdown :source="item.label" />
                </div>
                <checklist-item-comment
                  :checklist-item="item"
                  :comment-btn-id="`view-mandatory-checklist--${String(checklist.title).replaceAll(
                    ' ',
                    '-'
                  )}--content-type-comment--${index}`"
                  class="checklist-item-comment"
                ></checklist-item-comment>
              </div>
            </v-list>
          </v-list>
          <div class="pr-4">
            <v-checkbox
              id="`check-all-list`"
              v-model="isCheckedAll"
              color="primary"
              class="checkAllLabel"
              :checked="isCheckedAll"
              testing-id="checkAllCheckbox"
              :disabled="archivedExcursionDetected || !formControlEnabled"
              label="Select all"
              hide-details
              density="compact"
              @update:model-value="checkAllOnchange"
            ></v-checkbox>
            <div class="pt-1 pl-1">
              The School requires that the mandatory risk control requirements contained in this
              checklist are implemented for all excursions. I acknowledge that I have read and
              understood the measures contained in this checklist, and they have all been or will
              all be implemented.
            </div>
          </div>
        </div>
      </template>
    </cs-form-dialog>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import * as types from '@/store/mutationTypes';
import StbMarkdown from '@/components/StbMarkdown';
import excursionStatus from '../lib/const/excursionStatus';
import ChecklistItem from './ChecklistItem';
import ChecklistItemComment from './ChecklistItemComment';

export default {
  name: 'ViewMandatoryChecklist',

  components: {
    StbMarkdown,
    ChecklistItem,
    ChecklistItemComment
  },
  props: {
    elementDisabled: {
      type: Boolean,
      required: false,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    saveDraft: {
      type: Function,
      default: () => {}
    },
    formControlEnabled: Boolean
  },
  data: () => ({
    dialog: false,
    checklistChanged: true,
    removeLabel: 'The following risk controls have been selected by default.',
    originalChildren: {},
    changedChildren: {},
    isCheckedAll: false
  }),
  computed: {
    isTicked() {
      const checklist = this.$store.state.common.excursionDc.mandatoryChecklist.find(
        (l) => l.title === this.title
      );

      const checkedItem = this.getItemsByTitle(checklist);

      let itemTotal = 0;
      let checkedItemTotal = 0;

      if (checkedItem && checkedItem.length > 0) {
        checkedItem.forEach((i) => {
          if (i && i.children) {
            itemTotal += i.children.length;

            const checkedItems = i.children.filter((ic) => ic.ticked);
            if (checkedItems) {
              checkedItemTotal += checkedItems.length;
            }
          }
        });

        itemTotal += checkedItem.filter(
          (c) => (!c.children || c.children.length === 0) && c.contentType !== 'static-text'
        ).length;
        const checkedItems = checkedItem.filter((ic) => ic.ticked);
        if (checkedItems) {
          checkedItemTotal += checkedItems.length;
        }
      } else {
        checkedItemTotal = -1;
      }
      if (this.checklistChanged) {
        this.updateTickedMandatoryChecklists(this.title, itemTotal === checkedItemTotal);
      }
      return itemTotal === checkedItemTotal;
    },
    checklist() {
      return this.$store.state.common.excursionDc.mandatoryChecklist.find(
        (l) => l.title === this.title
      );
    },
    checklistCommentIconDisplayed() {
      const checklist = this.$store.state.common.excursionDc.mandatoryChecklist.find(
        (l) => l.title === this.title
      );

      let filledCommentCount = 0;
      if (checklist) {
        const checklistString = JSON.stringify(checklist);
        const emptyCommentCount = (checklistString.match(/"comment":""/g) || []).length;
        const commentCount = (checklistString.match(/"comment":/g) || []).length;
        filledCommentCount = commentCount - emptyCommentCount;
      }
      return filledCommentCount > 0;
    },
    getCompleteChecklistId() {
      return `view-mandatory-checklist--${this.title.replace(/\s*/g, '')}`;
    },
    archivedExcursionDetected() {
      return excursionStatus.ARCHIVED === this.$store.state.common.excursionDc.status;
    },
    btnClass() {
      return this.isTicked
        ? 'view-mandatory-checklist-button green-complete'
        : 'view-mandatory-checklist-button gray-incomplete';
    },
    getDlgBodyId() {
      return `dlg-view-mandatory-checklist-${this.title.replace(/\s*/g, '')}`;
    }
  },
  watch: {
    isTicked() {
      this.checklistChanged = true;
    }
  },
  methods: {
    getData() {
      this.dialog = true;
      this.originalChildren = this.getItemsByTitle(this.checklist);
      this.changedChildren = cloneDeep(this.originalChildren);
    },
    save() {
      this.dialog = false;
      const checklistModified = !isEqual(this.originalChildren, this.changedChildren);

      if (checklistModified) {
        this.$store.commit(types.COMMON_SET_MANDATORY_CHECKLIST, {
          title: this.title,
          checklist: this.changedChildren
        });
        this.$app.eventBus.$emit('setExcursionValuesChanged', true);
        this.saveDraft(null, true);
      }
    },
    close() {
      this.dialog = false;
      this.isCheckedAll = false;
    },
    getItemsByTitle(obj) {
      if (!obj.Strategy) {
        return [];
      }
      const checklist = obj.Strategy.data[Object.keys(obj.Strategy.data)].children;
      const result = cloneDeep(checklist);
      return result;
    },
    updateTickedMandatoryChecklists(checklistTitle, ticked) {
      if (
        this.$store.state.common.tickedMandatoryChecklists.length ===
          this.$store.state.common.mandatoryChecklistsLength &&
        this.checklistChanged === true
      ) {
        const checklist = this.$store.state.common.tickedMandatoryChecklists.find(
          (obj) => obj.title === checklistTitle
        );
        if (checklist && checklist.ticked !== ticked) {
          this.$store.commit(types.COMMON_SET_MANDATORY_CHECKLISTS_TICKED_BY_TITLE, {
            title: checklistTitle,
            ticked
          });
        }
      } else if (
        this.$store.state.common.tickedMandatoryChecklists.length <
        this.$store.state.common.mandatoryChecklistsLength
      ) {
        const tickedChecklist = {
          title: checklistTitle,
          ticked
        };
        this.$store.commit(types.COMMON_ADD_TICKED_MANDATORY_CHECKLISTS, tickedChecklist);
        this.checklistChanged = false;
      }
    },
    checkAllOnchange(value) {
      if (value) {
        this.changedChildren.forEach((item) => {
          const localItem = item;
          if (localItem.contentType === 'form-group') {
            localItem.children.forEach((option) => {
              const localOption = option;
              localOption.ticked = true;
            });
          } else if (localItem.contentType === 'checkbox') {
            localItem.ticked = true;
          }
        });
        this.changedChildren = cloneDeep(this.changedChildren);
        this.$store.commit(types.COMMON_SET_ALL_MANDATORY_CHECKLISTS_TICKED, true);
      }
    },
    checklistOnChanged(value) {
      if (!value) {
        this.isCheckedAll = false;
      }
    }
  }
};
</script>

<style scoped>
.checklist {
  height: 600px;
  padding: 0px 10px;
}

.checkAllLabel :deep(.v-label) {
  font-weight: bold !important;
  padding-left: 10px;
}

.view-mandatory-checklist-policy h1 {
  color: gray;
  width: 100%;
}

.view-mandatory-checklist-policy h2 {
  color: #ff7f32;
}

.view-mandatory-checklist-policy .v-list-subheader {
  font-size: 16px;
  padding-left: 0px !important;
}

.view-mandatory-checklist-policy .v-list-item {
  height: auto;
  align-items: flex-start;
  padding: 0px !important;
}

.view-mandatory-checklist-policy .content {
  max-width: 98%;
}

.green-complete :deep(.cs-button-icon-left) {
  color: #28a745 !important;
  font-size: 16pt;
}

.gray-incomplete :deep(.cs-button-icon-left) {
  color: #d3d3d3 !important;
}

.check-all-label {
  display: inline-block;
  padding-top: 0.4rem;
  font-size: 1rem;
  font-weight: bold;
}

.header-divider {
  color: #ff7f32;
  margin-bottom: 8px;
}

.checklist-item-comment {
  padding-left: 8px;
  padding-bottom: 8px;
}
</style>
